import type { SurveyModel } from "survey-core";
import { theme, Typography } from "antd";
import { createStyles } from "antd-style";
import { Button } from "@rumbledirect/ui";
import { ExternalLinkActions } from "../components/ExternalLinkActions";
import { ThankYouContent } from "../components/ThankYouContent";
import type { ContentBlock } from "../components/ThankYouContent";
import { formatAppointmentDateTime } from "../helpers/formatAppointmentDateTime";

const useStyles = createStyles(({ css }) => ({
  defaultButton: css`
    padding: 0 28px;
    font-size: 18px;
    font-weight: 700;
  `,
}));

type CompletedProps = {
  survey: SurveyModel;
};

export const Completed = ({ survey }: CompletedProps) => {
  const { token } = theme.useToken();
  const { styles } = useStyles();
  const {
    submitResponse,
    thankYouCardTitle,
    thankYouTitle,
    thankYouBody,
    showNewReportButton,
  } = survey;

  // SurveyJS doesn't reliably deserialize custom array properties; read from jsonObj as fallback
  const thankYouContent =
    survey.thankYouContent ??
    (survey as unknown as { jsonObj?: { thankYouContent?: unknown } }).jsonObj
      ?.thankYouContent;

  const thankYouVariables = {
    servicePartnerName: survey.getValue("selector__name") ?? null,
    appointmentDateTime:
      formatAppointmentDateTime(survey.getValue("appointmentTimeSlot__date")) ??
      null,
    calculatedRepairOrReplacement: survey.getVariable(
      "calculatedRepairOrReplacement",
    ),
  };

  const photos = survey.data?.photos;
  const showQrCode = photos
    ? Array.isArray(photos) && photos.includes("image_capture_qr_code")
    : true;

  return (
    <div
      style={{
        width: "100%",
        maxWidth: 920,
        background: "#ffffff",
        borderRadius: 10,
        overflow: "hidden",
        margin: "0 auto",
      }}
    >
      <div
        style={{
          height: 56,
          background: token.colorPrimary,
          color: "#ffffff",
          display: "flex",
          alignItems: "center",
          padding: "0 18px",
          fontWeight: 600,
          gap: 24,
          fontSize: 18,
        }}
      >
        <svg
          style={{
            width: 32,
            height: 32,
            marginBottom: 4,
            display: "inline-block",
          }}
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M8.5 7.5 9.6 6.2c.3-.4.8-.7 1.3-.7h2.2c.5 0 1 .2 1.3.7l1.1 1.3H19c1.1 0 2 .9 2 2v8.5c0 1.1-.9 2-2 2H5c-1.1 0-2-.9-2-2V9.5c0-1.1.9-2 2-2h3.5Z"
            stroke="white"
            strokeWidth="1.6"
            strokeLinejoin="round"
          />
          <path
            d="M12 17.2a3.6 3.6 0 1 0 0-7.2 3.6 3.6 0 0 0 0 7.2Z"
            stroke="white"
            strokeWidth="1.6"
          />
        </svg>
        {thankYouCardTitle}
      </div>

      <div
        style={{
          padding: "24px",
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          borderLeft: `1px solid ${token?.colorBorderSecondary ?? "#d8d8d8"}`,
          borderBottom: `1px solid ${token?.colorBorderSecondary ?? "#d8d8d8"}`,
          borderRight: `1px solid ${token?.colorBorderSecondary ?? "#d8d8d8"}`,
          borderRadius: "0 0 10px 10px",
        }}
      >
        <div
          style={{
            width: 72,
            height: 72,
            borderRadius: "100%",
            border: "6px solid #111",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          aria-hidden="true"
        >
          <svg
            style={{ width: 46, height: 46 }}
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M20 7.5 10.2 17.3 4 11.1"
              stroke="#111"
              strokeWidth="2.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <Typography.Title
          level={4}
          style={{ marginBottom: 20, marginTop: 30, fontWeight: 700 }}
        >
          {thankYouTitle}
        </Typography.Title>

        {Array.isArray(thankYouContent) && thankYouContent.length > 0 ? (
          <ThankYouContent
            blocks={thankYouContent as ContentBlock[]}
            variables={thankYouVariables}
          />
        ) : (
          <Typography.Paragraph
            style={{ fontSize: 15, fontWeight: 500, color: "#555" }}
          >
            {thankYouBody}
          </Typography.Paragraph>
        )}

        {showQrCode && submitResponse?.imageCapture?.url ? (
          <ExternalLinkActions externalLink={submitResponse.imageCapture.url} />
        ) : null}

        {showNewReportButton ? (
          <Button
            type="default"
            size="large"
            className={styles.defaultButton}
            style={{ marginTop: 40 }}
            href="#"
            onClick={(e) => {
              e.preventDefault();
              survey.clear(true, true);
            }}
          >
            Nieuwe schademelding
          </Button>
        ) : null}
      </div>
    </div>
  );
};
